import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  DeleteIcon,
  Input,
  PlusIcon,
  Select,
  TextField,
} from "@pd/pd-ui-kit";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Modal from "~/shared/ui/Fields/Modal";
import { useGetDealersForSelect } from "~/entity/Dealers";
import {
  ITariffDealerDiscountParams,
  useGetBaseTariff,
  useSaveBaseTariff,
} from "~/entity/Tariffs";
import { IBaseTariffRow } from "~/pages/Directory/BaseTariffs/model/baseTariffRow";
import { getFieldError } from "~/pages/Directory/shared/getFieldError";
import { handlePopupScroll } from "~/pages/Directory/shared/handlePopupScroll";
import { useTariffFormModal } from "~/pages/Directory/shared/useTariffFormModal";
import { parseNumeric } from "~/shared/lib/parseNumeric";

import EmployeeSelect, { ISelectValue } from "./EmployeeSelect";
import { getBaseTariffFormValidation } from "./validation";

import s from "~/pages/Directory/shared/TariffFormModal.less";

interface IDealerDiscountForm {
  dealers: ISelectValue[];
  percent: string;
  amount: string;
  priceWithDiscount: string;
}

interface IEmployeeDiscountForm {
  dealer: ISelectValue | null;
  employee: ISelectValue | null;
  percent: string;
  amount: string;
  priceWithDiscount: string;
}

type DiscountSource = "percent" | "amount";
type DiscountFieldName = "dealerDiscounts" | "employeeDiscounts";

const DISCOUNT_RECALC_DELAY = 400;

export interface IBaseTariffForm {
  price: string;
  dealerDiscounts: IDealerDiscountForm[];
  employeeDiscounts: IEmployeeDiscountForm[];
}

interface BaseTariffFormModalProps {
  rows: IBaseTariffRow[];
}

const emptyDealerDiscount: IDealerDiscountForm = {
  dealers: [],
  percent: "",
  amount: "",
  priceWithDiscount: "",
};

const emptyEmployeeDiscount: IEmployeeDiscountForm = {
  dealer: null,
  employee: null,
  percent: "",
  amount: "",
  priceWithDiscount: "",
};

const defaultValues: IBaseTariffForm = {
  price: "",
  dealerDiscounts: [],
  employeeDiscounts: [],
};

const formatNumber = (value: number) => String(Math.round(value * 100) / 100);

const formatPrice = (value: string) => {
  const parsed = parseNumeric(value);
  return parsed === null ? "" : formatNumber(parsed);
};

const clampPercentInput = (raw: string) => {
  if (!raw) {
    return raw;
  }
  const parsed = parseNumeric(raw);
  if (parsed === null) {
    return raw;
  }
  if (parsed > 100) {
    return "100";
  }
  if (parsed < 0) {
    return "0";
  }
  return raw;
};

const deriveDiscountValues = (
  price: string,
  percent: string,
  amount: string,
  priceWithDiscount: string,
) => {
  const parsedPrice = parseNumeric(price);
  let nextAmount = amount ? formatPrice(amount) : "";
  let nextPercent = percent ? formatPrice(percent) : "";
  let nextPriceWithDiscount = priceWithDiscount
    ? formatPrice(priceWithDiscount)
    : "";

  if (parsedPrice !== null) {
    if (!nextAmount) {
      const pwd = parseNumeric(priceWithDiscount);
      const pct = parseNumeric(percent);
      if (pwd !== null) {
        nextAmount = formatNumber(Math.max(0, parsedPrice - pwd));
      } else if (pct !== null) {
        nextAmount = formatNumber((parsedPrice * pct) / 100);
      }
    }

    if (!nextPercent && parsedPrice !== 0) {
      const amt = parseNumeric(nextAmount);
      if (amt !== null) {
        nextPercent = formatNumber((amt / parsedPrice) * 100);
      }
    }

    if (!nextPriceWithDiscount) {
      const amt = parseNumeric(nextAmount);
      if (amt !== null) {
        nextPriceWithDiscount = formatNumber(Math.max(0, parsedPrice - amt));
      }
    }
  }

  return {
    percent: nextPercent,
    amount: nextAmount,
    priceWithDiscount: nextPriceWithDiscount,
  };
};

const BaseTariffFormModal = ({ rows }: BaseTariffFormModalProps) => {
  const { t } = useTranslation();
  const { isOpen, currentId, closeModal } = useTariffFormModal(
    "Create",
    "Edit",
  );

  const currentRow = useMemo(
    () => rows.find((row) => row.id === currentId),
    [rows, currentId],
  );
  const tariffId = currentId;

  const { tariff } = useGetBaseTariff(isOpen ? tariffId : null);
  const { updateTariff } = useSaveBaseTariff();

  const dealersSelect = useGetDealersForSelect(isOpen);
  const employeeDealersSelect = useGetDealersForSelect(isOpen);

  const [duplicateError, setDuplicateError] = useState("");
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [preservedDealerDiscounts, setPreservedDealerDiscounts] = useState<
    ITariffDealerDiscountParams[]
  >([]);

  const {
    control,
    errors,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm<IBaseTariffForm>({
    resolver: yupResolver(getBaseTariffFormValidation(t)),
    defaultValues,
  });

  const dealerArray = useFieldArray<IDealerDiscountForm>({
    control,
    name: "dealerDiscounts",
  });
  const employeeArray = useFieldArray<IEmployeeDiscountForm>({
    control,
    name: "employeeDiscounts",
  });

  const dealerDiscountsValue = watch("dealerDiscounts");
  const employeeDiscountsValue = watch("employeeDiscounts");
  const recalcTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const lastDiscountSourceRef = useRef<
    Record<string, { source: DiscountSource; value: string }>
  >({});
  const discountsSnapshotRef = useRef({
    dealerDiscounts: [] as IDealerDiscountForm[],
    employeeDiscounts: [] as IEmployeeDiscountForm[],
  });
  discountsSnapshotRef.current = {
    dealerDiscounts: dealerDiscountsValue || [],
    employeeDiscounts: employeeDiscountsValue || [],
  };

  useEffect(() => {
    return () => {
      Object.values(recalcTimers.current).forEach((timer) =>
        clearTimeout(timer),
      );
    };
  }, []);

  const dealerOptions = useMemo(() => {
    const selected: ISelectValue[] = [];
    (dealerDiscountsValue || []).forEach((block) => {
      (block?.dealers || []).forEach((dealer) => {
        if (dealer && !selected.some((item) => item.value === dealer.value)) {
          selected.push(dealer);
        }
      });
    });

    const missing = selected.filter(
      (item) =>
        !dealersSelect.options.some((option) => option.value === item.value),
    );

    return [...dealersSelect.options, ...missing];
  }, [dealersSelect.options, dealerDiscountsValue]);

  const getAvailableDealerOptions = (blockIndex: number) => {
    const takenInOtherBlocks = new Set<string>();
    (dealerDiscountsValue || []).forEach((block, i) => {
      if (i === blockIndex) {
        return;
      }
      (block?.dealers || []).forEach((dealer) => {
        takenInOtherBlocks.add(dealer.value);
      });
    });
    return dealerOptions.filter(
      (option) => !takenInOtherBlocks.has(option.value),
    );
  };

  const getTakenEmployeeIds = (blockIndex: number): string[] => {
    const ids: string[] = [];
    (employeeDiscountsValue || []).forEach((block, i) => {
      if (i !== blockIndex && block?.employee?.value) {
        ids.push(block.employee.value);
      }
    });
    return ids;
  };

  const positionInstanceIds = useMemo(
    () => (tariff?.positions || []).map((position) => position.instanceId),
    [tariff],
  );

  const employeeDealerOptions = useMemo(() => {
    const missing: ISelectValue[] = [];
    (employeeDiscountsValue || []).forEach((block) => {
      const dealer = block?.dealer;
      if (
        dealer &&
        !employeeDealersSelect.options.some(
          (option) => option.value === dealer.value,
        ) &&
        !missing.some((item) => item.value === dealer.value)
      ) {
        missing.push(dealer);
      }
    });
    return [...employeeDealersSelect.options, ...missing];
  }, [employeeDealersSelect.options, employeeDiscountsValue]);

  useEffect(() => {
    if (!isOpen) {
      reset(defaultValues);
      setDuplicateError("");
      setIsPrefilling(false);
      setPreservedDealerDiscounts([]);
      lastDiscountSourceRef.current = {};
      return;
    }

    if (!tariff) {
      setIsPrefilling(true);
      return;
    }

    const preserved: ITariffDealerDiscountParams[] = [];
    const dealerGroups = new Map<
      string,
      {
        percent: string;
        amount: string;
        priceWithDiscount: string;
        dealers: ISelectValue[];
      }
    >();

    tariff.dealerDiscounts.forEach((discount) => {
      if (discount.dealerId == null) {
        preserved.push({
          organizationId: discount.organizationId || undefined,
          discountPercent: discount.discountPercent || undefined,
          discountAmount: discount.discountAmount || undefined,
          priceWithDiscount: discount.priceWithDiscount || undefined,
        });
        return;
      }

      const key = [
        discount.discountPercent,
        discount.discountAmount,
        discount.priceWithDiscount,
      ].join("|");
      const group = dealerGroups.get(key) || {
        ...deriveDiscountValues(
          tariff.price,
          discount.discountPercent || "",
          discount.discountAmount || "",
          discount.priceWithDiscount || "",
        ),
        dealers: [],
      };
      group.dealers.push({
        value: String(discount.dealerId),
        label: discount.dealerName || String(discount.dealerId),
      });
      dealerGroups.set(key, group);
    });

    const employeeDiscounts: IEmployeeDiscountForm[] = tariff.employeeDiscounts
      .filter((discount) => !discount.deletedAt)
      .map((discount) => ({
        dealer:
          discount.dealerId != null
            ? {
                value: String(discount.dealerId),
                label: discount.dealerName || String(discount.dealerId),
              }
            : null,
        employee: {
          value: String(discount.employeeId),
          label: discount.employeeName || String(discount.employeeId),
        },
        ...deriveDiscountValues(
          tariff.price,
          discount.discountPercent || "",
          discount.discountAmount || "",
          discount.priceWithDiscount || "",
        ),
      }));

    const dealerDiscounts: IDealerDiscountForm[] = [];
    dealerGroups.forEach((group) => {
      dealerDiscounts.push({
        dealers: group.dealers,
        percent: group.percent,
        amount: group.amount,
        priceWithDiscount: group.priceWithDiscount,
      });
    });

    setPreservedDealerDiscounts(preserved);
    reset({
      price: formatPrice(tariff.price),
      dealerDiscounts,
      employeeDiscounts,
    });
    setIsPrefilling(false);
  }, [isOpen, tariff, reset]);

  const requirePrice = useCallback((): number | null => {
    const parsed = parseNumeric(getValues("price") || "");
    if (parsed === null || parsed <= 0) {
      setError("price", {
        type: "manual",
        message: t("directory.baseTariffs.modal.priceRequired"),
      });
      return null;
    }
    clearErrors("price");
    return parsed;
  }, [clearErrors, getValues, setError, t]);

  const applyDiscountFromSource = useCallback(
    (
      field: DiscountFieldName,
      index: number,
      source: DiscountSource,
      rawValue: string,
      priceOverride?: number | null,
    ) => {
      const rawPrice =
        priceOverride !== undefined
          ? priceOverride
          : parseNumeric(getValues("price") || "");
      const price = rawPrice === null || rawPrice <= 0 ? null : rawPrice;
      if (price === null) {
        if (source === "percent") {
          setValue(`${field}.${index}.amount`, "");
        } else {
          setValue(`${field}.${index}.percent`, "");
        }
        setValue(`${field}.${index}.priceWithDiscount`, "");
        return;
      }

      const value = parseNumeric(rawValue);
      if (value === null) {
        if (source === "percent") {
          setValue(`${field}.${index}.amount`, "");
        } else {
          setValue(`${field}.${index}.percent`, "");
        }
        setValue(`${field}.${index}.priceWithDiscount`, "");
        return;
      }

      if (source === "percent") {
        const percent = Math.min(100, Math.max(0, value));
        const amount = (price * percent) / 100;
        setValue(`${field}.${index}.amount`, formatNumber(amount), {
          shouldDirty: true,
        });
        setValue(
          `${field}.${index}.priceWithDiscount`,
          formatNumber(Math.max(0, price - amount)),
          { shouldDirty: true },
        );
        return;
      }

      const nextPercent =
        price === 0 ? "" : formatNumber(Math.min(100, (value / price) * 100));
      setValue(`${field}.${index}.percent`, nextPercent, {
        shouldDirty: true,
      });
      setValue(
        `${field}.${index}.priceWithDiscount`,
        formatNumber(Math.max(0, price - value)),
        { shouldDirty: true },
      );
    },
    [getValues, setValue],
  );

  const scheduleDiscountRecalc = useCallback(
    (
      field: DiscountFieldName,
      index: number,
      source: DiscountSource,
      rawValue: string,
      immediate = false,
    ) => {
      const key = `${field}.${index}.${source}`;
      const existing = recalcTimers.current[key];
      if (existing) {
        clearTimeout(existing);
      }

      const run = () => {
        delete recalcTimers.current[key];
        applyDiscountFromSource(field, index, source, rawValue);
      };

      if (immediate) {
        run();
        return;
      }

      recalcTimers.current[key] = setTimeout(run, DISCOUNT_RECALC_DELAY);
    },
    [applyDiscountFromSource],
  );

  const handleDiscountFieldChange = (
    field: DiscountFieldName,
    index: number,
    source: DiscountSource,
    rawValue: string,
    onChange: (value: string) => void,
    immediate = false,
  ) => {
    onChange(rawValue);
    lastDiscountSourceRef.current[`${field}.${index}`] = {
      source,
      value: rawValue,
    };
    const price = requirePrice();
    if (price === null) {
      const key = `${field}.${index}.${source}`;
      const existing = recalcTimers.current[key];
      if (existing) {
        clearTimeout(existing);
        delete recalcTimers.current[key];
      }
      applyDiscountFromSource(field, index, source, rawValue, null);
      return;
    }
    scheduleDiscountRecalc(field, index, source, rawValue, immediate);
  };

  const recalcAllDiscounts = (price: string) => {
    const parsedPrice = parseNumeric(price);
    if (parsedPrice !== null && parsedPrice > 0) {
      clearErrors("price");
    } else if (
      (dealerArray.fields.length > 0 || employeeArray.fields.length > 0) &&
      (parsedPrice === null || parsedPrice <= 0)
    ) {
      setError("price", {
        type: "manual",
        message: t("directory.baseTariffs.modal.priceRequired"),
      });
    }

    (["dealerDiscounts", "employeeDiscounts"] as DiscountFieldName[]).forEach(
      (field) => {
        const fieldsCount =
          field === "dealerDiscounts"
            ? dealerArray.fields.length
            : employeeArray.fields.length;
        const blocks = getValues(field) || [];
        const watchedBlocks = discountsSnapshotRef.current[field] || [];
        const count = Math.max(
          fieldsCount,
          blocks.length,
          watchedBlocks.length,
        );

        for (let index = 0; index < count; index += 1) {
          const tracked = lastDiscountSourceRef.current[`${field}.${index}`];
          const percent =
            (getValues(`${field}.${index}.percent` as const) as string) ||
            blocks[index]?.percent ||
            watchedBlocks[index]?.percent ||
            (tracked?.source === "percent" ? tracked.value : "") ||
            "";
          const amount =
            (getValues(`${field}.${index}.amount` as const) as string) ||
            blocks[index]?.amount ||
            watchedBlocks[index]?.amount ||
            (tracked?.source === "amount" ? tracked.value : "") ||
            "";

          // При смене цены тарифа сохраняем процент и пересчитываем сумму/цену со скидкой
          if (parseNumeric(percent) !== null) {
            applyDiscountFromSource(
              field,
              index,
              "percent",
              percent,
              parsedPrice,
            );
            lastDiscountSourceRef.current[`${field}.${index}`] = {
              source: "percent",
              value: percent,
            };
            continue;
          }
          if (parseNumeric(amount) !== null) {
            applyDiscountFromSource(
              field,
              index,
              "amount",
              amount,
              parsedPrice,
            );
            lastDiscountSourceRef.current[`${field}.${index}`] = {
              source: "amount",
              value: amount,
            };
          }
        }
      },
    );
  };

  const schedulePriceRecalc = (price: string, immediate = false) => {
    const key = "price";
    const existing = recalcTimers.current[key];
    if (existing) {
      clearTimeout(existing);
    }

    const run = () => {
      delete recalcTimers.current[key];
      recalcAllDiscounts(price);
    };

    if (immediate) {
      run();
      return;
    }

    recalcTimers.current[key] = setTimeout(run, DISCOUNT_RECALC_DELAY);
  };

  const flushPendingRecalcs = () => {
    Object.entries(recalcTimers.current).forEach(([key, timer]) => {
      clearTimeout(timer);
      delete recalcTimers.current[key];

      if (key === "price") {
        recalcAllDiscounts(getValues("price") || "");
        return;
      }

      const [field, indexRaw, source] = key.split(".") as [
        DiscountFieldName,
        string,
        DiscountSource,
      ];
      const index = Number(indexRaw);
      const block = getValues(`${field}.${index}` as const);
      if (!block) {
        return;
      }
      applyDiscountFromSource(field, index, source, block[source] || "");
    });
  };

  const handleRemoveDiscount = (
    field: DiscountFieldName,
    index: number,
  ): void => {
    if (field === "dealerDiscounts") {
      dealerArray.remove(index);
    } else {
      employeeArray.remove(index);
    }

    const dealersLeft =
      (dealerDiscountsValue?.length ?? 0) -
      (field === "dealerDiscounts" ? 1 : 0);
    const employeesLeft =
      (employeeDiscountsValue?.length ?? 0) -
      (field === "employeeDiscounts" ? 1 : 0);

    if (dealersLeft + employeesLeft === 0) {
      clearErrors("price");
    }
  };

  const buildDiscountPayload = (
    percent: string,
    amount: string,
    priceWithDiscount: string,
  ) => ({
    discountPercent: percent ? formatNumber(Number(percent)) : undefined,
    discountAmount: amount ? formatNumber(Number(amount)) : undefined,
    priceWithDiscount: priceWithDiscount
      ? formatNumber(Number(priceWithDiscount))
      : undefined,
  });

  const submit = () => {
    flushPendingRecalcs();
    handleSubmit((data) => {
      const seen = new Map<string, string>();
      const duplicates = new Set<string>();
      data.dealerDiscounts.forEach((block) => {
        block.dealers.forEach((dealer) => {
          if (seen.has(dealer.value)) {
            duplicates.add(seen.get(dealer.value) || dealer.label);
          } else {
            seen.set(dealer.value, dealer.label);
          }
        });
      });

      if (duplicates.size) {
        setDuplicateError(
          t("directory.baseTariffs.modal.duplicateDealers", {
            names: Array.from(duplicates).join(", "),
          }),
        );
        return;
      }
      setDuplicateError("");

      const dealerDiscounts: ITariffDealerDiscountParams[] = [
        ...preservedDealerDiscounts,
      ];
      data.dealerDiscounts.forEach((block) => {
        const discount = buildDiscountPayload(
          block.percent,
          block.amount,
          block.priceWithDiscount,
        );
        block.dealers.forEach((dealer) => {
          dealerDiscounts.push({ dealerId: Number(dealer.value), ...discount });
        });
      });

      const employeeDiscounts = data.employeeDiscounts.map((block) => ({
        employeeId: Number(block.employee!.value),
        ...buildDiscountPayload(
          block.percent,
          block.amount,
          block.priceWithDiscount,
        ),
      }));

      if (tariffId) {
        updateTariff.mutate(
          {
            id: tariffId,
            data: {
              price: formatPrice(data.price) || "0",
              dealerDiscounts,
              employeeDiscounts,
            },
          },
          { onSuccess: closeModal },
        );
      }
    })();
  };

  const renderRemoveButton = (field: DiscountFieldName, index: number) => (
    <Button
      variant="text"
      color="secondary"
      size="small"
      htmlType="button"
      icon={<DeleteIcon size={16} />}
      aria-label={t("ui.delete")}
      title={t("ui.delete")}
      className={s.removeButton}
      onClick={() => handleRemoveDiscount(field, index)}
    />
  );

  const renderAddDiscountButton = (append: () => void) => (
    <button
      type="button"
      className={s.addPosition}
      onClick={() => {
        requirePrice();
        append();
      }}
    >
      <PlusIcon size={16} fill="#e37639" />
      {t("directory.baseTariffs.modal.addDiscount")}
    </button>
  );

  const renderDiscountFields = (
    field: DiscountFieldName,
    index: number,
    item: Partial<IDealerDiscountForm> | Partial<IEmployeeDiscountForm>,
  ) => (
    <div className={s.discountRow}>
      <div className={s.fieldWrapper}>
        <span className={s.label}>
          {t("directory.baseTariffs.modal.percent")}
        </span>
        <Controller
          name={`${field}.${index}.percent`}
          control={control}
          defaultValue={item.percent}
          render={({ onChange, value, onBlur }) => (
            <Input
              value={value}
              onChange={(event) => {
                const nextValue = clampPercentInput(event.target.value);
                handleDiscountFieldChange(
                  field,
                  index,
                  "percent",
                  nextValue,
                  onChange,
                );
              }}
              onFocus={() => {
                requirePrice();
              }}
              onBlur={() => {
                handleDiscountFieldChange(
                  field,
                  index,
                  "percent",
                  clampPercentInput(
                    getValues(`${field}.${index}.percent`) || "",
                  ),
                  onChange,
                  true,
                );
                onBlur();
              }}
              status={errors?.[field]?.[index]?.percent ? "error" : undefined}
              errorText={errors?.[field]?.[index]?.percent?.message}
              placeholder={t("directory.filter.inputPlaceholder")}
            />
          )}
        />
      </div>
      <div className={s.fieldWrapper}>
        <span className={s.label}>
          {t("directory.baseTariffs.modal.amount")}
        </span>
        <Controller
          name={`${field}.${index}.amount`}
          control={control}
          defaultValue={item.amount}
          render={({ onChange, value, onBlur }) => (
            <Input
              value={value}
              onChange={(event) =>
                handleDiscountFieldChange(
                  field,
                  index,
                  "amount",
                  event.target.value,
                  onChange,
                )
              }
              onFocus={() => {
                requirePrice();
              }}
              onBlur={() => {
                handleDiscountFieldChange(
                  field,
                  index,
                  "amount",
                  getValues(`${field}.${index}.amount`) || "",
                  onChange,
                  true,
                );
                onBlur();
              }}
              status={errors?.[field]?.[index]?.amount ? "error" : undefined}
              errorText={errors?.[field]?.[index]?.amount?.message}
              placeholder={t("directory.filter.inputPlaceholder")}
            />
          )}
        />
      </div>
      <div className={s.fieldWrapper}>
        <span className={s.label}>
          {t("directory.baseTariffs.modal.priceWithDiscount")}
        </span>
        <Controller
          name={`${field}.${index}.priceWithDiscount`}
          control={control}
          defaultValue={item.priceWithDiscount}
          render={({ value }) => (
            <Input value={value} disabled className={s.readOnlyInput} />
          )}
        />
      </div>
    </div>
  );

  const positionLines = currentRow?.positions?.length
    ? currentRow.positions
    : (tariff?.positions ?? []).map(
        (position) => `${position.code}. ${position.name}`,
      );

  return (
    <Modal
      isOpen={isOpen}
      title={t("directory.baseTariffs.modal.title")}
      actionClose={closeModal}
      customClassName={s.baseTariffModal}
      w={600}
    >
      <div className={s.form}>
        <div className={s.row}>
          <div className={s.fieldWrapper}>
            <span className={s.label}>
              {t("directory.baseTariffs.modal.position")}
            </span>
            <div>
              {positionLines.length ? (
                positionLines.map((position, index) => (
                  <TextField
                    key={position}
                    value={`${position}${
                      index < positionLines.length - 1 ? "," : ""
                    }`}
                  />
                ))
              ) : (
                <TextField value="—" />
              )}
            </div>
          </div>
          <div className={s.fieldWrapper}>
            <span className={s.label}>
              {t("directory.baseTariffs.modal.price")}
            </span>
            <Controller
              name="price"
              control={control}
              render={({ onChange, value, onBlur }) => (
                <Input
                  value={value}
                  onChange={(event) => {
                    onChange(event.target.value);
                    const parsed = parseNumeric(event.target.value);
                    if (parsed !== null && parsed > 0) {
                      clearErrors("price");
                    }
                    schedulePriceRecalc(event.target.value);
                  }}
                  onBlur={() => {
                    schedulePriceRecalc(getValues("price") || "", true);
                    onBlur();
                  }}
                  status={errors?.price ? "error" : undefined}
                  errorText={errors?.price?.message}
                  placeholder={t("directory.filter.inputPlaceholder")}
                />
              )}
            />
          </div>
        </div>

        <div className={s.section}>
          <p className={s.sectionTitle}>
            {t("directory.baseTariffs.modal.dealerSection")}
          </p>

          {dealerArray.fields.map((field, index) => (
            <div
              key={field.id}
              className={clsx(s.discountBlock, s.discountBlockDealers)}
            >
              <div className={clsx(s.discountHead, s.discountHeadDealers)}>
                <div className={s.fieldWrapper}>
                  <span className={s.label}>
                    {t("directory.baseTariffs.modal.dealers")}
                  </span>
                  <Controller
                    name={`dealerDiscounts.${index}.dealers`}
                    control={control}
                    defaultValue={field.dealers}
                    render={({ onChange, value }) => (
                      <Select
                        mode="multiple"
                        labelInValue
                        showSearch
                        filterOption={false}
                        value={value}
                        onChange={(options) => onChange(options || [])}
                        options={getAvailableDealerOptions(index)}
                        loading={dealersSelect.loading}
                        onSearch={dealersSelect.onSearch}
                        onOpenChange={dealersSelect.onOpenChange}
                        onPopupScroll={(event) =>
                          handlePopupScroll(event, dealersSelect.onScrollBottom)
                        }
                        placeholder={t(
                          "directory.baseTariffs.modal.selectPlaceholder",
                        )}
                      />
                    )}
                  />
                  {getFieldError(errors?.dealerDiscounts?.[index]?.dealers) && (
                    <span className={s.error}>
                      {getFieldError(errors?.dealerDiscounts?.[index]?.dealers)}
                    </span>
                  )}
                </div>
                {renderRemoveButton("dealerDiscounts", index)}
              </div>
              {renderDiscountFields("dealerDiscounts", index, field)}
            </div>
          ))}

          {renderAddDiscountButton(() =>
            dealerArray.append(emptyDealerDiscount),
          )}
        </div>

        <div className={s.divider} />

        <div className={s.section}>
          <p className={s.sectionTitle}>
            {t("directory.baseTariffs.modal.employeeSection")}
          </p>

          {employeeArray.fields.map((field, index) => (
            <div key={field.id} className={s.discountBlock}>
              <div className={s.discountHead}>
                <div className={s.fieldWrapper}>
                  <span className={s.label}>
                    {t("directory.baseTariffs.modal.dealer")}
                  </span>
                  <Controller
                    name={`employeeDiscounts.${index}.dealer`}
                    control={control}
                    defaultValue={field.dealer}
                    render={({ onChange, value }) => (
                      <Select
                        labelInValue
                        allowClear
                        showSearch
                        filterOption={false}
                        value={value}
                        onChange={(option) => {
                          onChange(option ?? null);
                          setValue(`employeeDiscounts.${index}.employee`, null);
                        }}
                        options={employeeDealerOptions}
                        loading={employeeDealersSelect.loading}
                        onSearch={employeeDealersSelect.onSearch}
                        onOpenChange={employeeDealersSelect.onOpenChange}
                        onPopupScroll={(event) =>
                          handlePopupScroll(
                            event,
                            employeeDealersSelect.onScrollBottom,
                          )
                        }
                        status={
                          errors?.employeeDiscounts?.[index]?.dealer
                            ? "error"
                            : undefined
                        }
                        placeholder={t(
                          "directory.baseTariffs.modal.selectPlaceholder",
                        )}
                      />
                    )}
                  />
                  {errors?.employeeDiscounts?.[index]?.dealer && (
                    <span className={s.error}>{t("validation.required")}</span>
                  )}
                </div>
                <div className={s.fieldWrapper}>
                  <span className={s.label}>
                    {t("directory.baseTariffs.modal.employee")}
                  </span>
                  <Controller
                    name={`employeeDiscounts.${index}.employee`}
                    control={control}
                    defaultValue={field.employee}
                    render={({ onChange, value }) => (
                      <EmployeeSelect
                        enabled={isOpen}
                        dealerId={
                          employeeDiscountsValue?.[index]?.dealer
                            ? Number(
                                employeeDiscountsValue[index].dealer!.value,
                              )
                            : undefined
                        }
                        positionInstanceIds={positionInstanceIds}
                        excludeValues={getTakenEmployeeIds(index)}
                        value={value}
                        onChange={onChange}
                        hasError={
                          !!errors?.employeeDiscounts?.[index]?.employee
                        }
                      />
                    )}
                  />
                  {errors?.employeeDiscounts?.[index]?.employee && (
                    <span className={s.error}>{t("validation.required")}</span>
                  )}
                </div>
                {renderRemoveButton("employeeDiscounts", index)}
              </div>
              {renderDiscountFields("employeeDiscounts", index, field)}
            </div>
          ))}

          {renderAddDiscountButton(() =>
            employeeArray.append(emptyEmployeeDiscount),
          )}
        </div>

        {duplicateError && <span className={s.error}>{duplicateError}</span>}

        <div className={s.buttons}>
          <Button
            bold
            color="secondary"
            fontSize="14"
            size="medium"
            radius={5}
            onClick={closeModal}
          >
            {t("directory.baseTariffs.modal.cancel")}
          </Button>
          <Button
            bold
            color="primary"
            fontSize="14"
            size="medium"
            radius={5}
            disabled={!tariff || isPrefilling}
            onClick={submit}
          >
            {t("directory.baseTariffs.modal.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BaseTariffFormModal;
